import { ColDef } from 'ag-grid-community';
import { ColumnMeta, PredefinedColumn } from '../../../../../../types';
import { COMPANY_VIEW_TYPE } from '../../../../../../data-models/view-config.data-model';
import { buildHeaderTooltip } from '../buildHeaderTooltip';
import { getFieldTooltipContent } from '../field-tooltip-content';
import { CompanyNameAndLogo } from './CompanyNameAndLogo/CompanyNameAndLogo';
import { DrillDown } from './DrillDown/DrillDown';
import { CompanyRT } from './CompanyNameAndLogo/CompanyRT';

type ColumnsMap = Record<COMPANY_VIEW_TYPE, Record<string, PredefinedColumn>>;
export class PredefinedColumns {
  static columnsByView: ColumnsMap = {
    [COMPANY_VIEW_TYPE.PORTFOLIO_OVERVIEW]: {
      [CompanyNameAndLogo.NAME]: new CompanyNameAndLogo(),
      [DrillDown.NAME]: new DrillDown(),
    },
    [COMPANY_VIEW_TYPE.RETURN_FORECAST]: {
      [CompanyNameAndLogo.NAME]: new CompanyNameAndLogo(),
      [DrillDown.NAME]: new DrillDown(),
    },
    [COMPANY_VIEW_TYPE.RETURN_FORECAST_2]: {
      [CompanyNameAndLogo.NAME]: new CompanyNameAndLogo(),
      [DrillDown.NAME]: new DrillDown(),
    },
    [COMPANY_VIEW_TYPE.ROUND_TRACKER]: {
      [CompanyRT.NAME]: new CompanyRT(),
    },
  };

  static getAllMeta(companyViewType: COMPANY_VIEW_TYPE): ColumnMeta[] {
    if (!companyViewType) {
      return [];
    }

    // Non-company views (e.g. the seeded security view's legacy "SECURITY_OVERVIEW")
    // carry companyViewType values outside the enum — they have no chrome columns.
    const viewPredefinedColumns = PredefinedColumns.columnsByView[companyViewType] ?? {};

    return Object.values(viewPredefinedColumns).map((colConf) => colConf.getMeta());
  }

  static getDefinition(columnName: string, companyViewType?: COMPANY_VIEW_TYPE): ColDef | null {
    if (!companyViewType) {
      return null;
    }

    const column = PredefinedColumns.columnsByView[companyViewType]?.[columnName];
    if (!column) {
      return null;
    }

    const definition = column.getDefinition();

    // Compose the same multi-line header tooltip
    // (name/description/source/formula) used for dynamic columns. Keyed by the
    // predefined column's identity (meta.path || meta.name); falls back to any
    // hardcoded headerTooltip already on the definition. Returns a copy so the
    // shared definition instance is not mutated; undefined tooltip (e.g. the
    // DrillDown action column) leaves the header without a tooltip.
    const meta = column.getMeta();
    const headerTooltip = buildHeaderTooltip({
      displayName: meta.displayName,
      fallbackDescription: (definition.headerTooltip as string | undefined) ?? meta.description,
      content: getFieldTooltipContent(meta.path || meta.name),
    });

    return { ...definition, headerTooltip };
  }
}
